import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface UseRealtimeStatusOptions {
  table: string;
  userId: string | undefined;
  itemIds: string[];
  terminalStatuses?: string[];
  onStatusChange: (id: string, newRow: Record<string, unknown>) => void;
  enabled?: boolean;
}

export function useRealtimeStatus({
  table,
  userId,
  itemIds,
  terminalStatuses = ['ready', 'error'],
  onStatusChange,
  enabled = true,
}: UseRealtimeStatusOptions) {
  const callbackRef = useRef(onStatusChange);
  callbackRef.current = onStatusChange;

  const itemIdsRef = useRef(itemIds);
  itemIdsRef.current = itemIds;

  const terminalRef = useRef(terminalStatuses);
  terminalRef.current = terminalStatuses;

  const hasItems = itemIds.length > 0;

  useEffect(() => {
    if (!userId || !enabled || !hasItems) return;

    const channelName = `realtime-${table}-${userId}`;

    const channel: RealtimeChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table,
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newRow = payload.new as Record<string, unknown>;
          const id = newRow.id as string;
          const status = newRow.status as string;

          if (
            itemIdsRef.current.includes(id) &&
            terminalRef.current.includes(status)
          ) {
            callbackRef.current(id, newRow);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, userId, enabled, hasItems]);
}
